import {
  File as FileIcon,
  FileArchive,
  FileAudio,
  FileImage,
  FileSpreadsheet,
  FileText,
  FileVideo,
  type LucideIcon,
} from 'lucide-react';
import type { EventAttachment } from './event-attachments';

export type AttachmentViewer = 'image' | 'pdf' | 'video' | 'audio' | 'text' | 'none';

export interface AttachmentKind {
  isImage: boolean;
  viewer: AttachmentViewer;
  Icon: LucideIcon;
  color: string;
}

const COLOR = {
  image: '#34d399',
  pdf: '#f87171',
  doc: '#60a5fa',
  sheet: '#4ade80',
  archive: '#fbbf24',
  audio: '#c084fc',
  video: '#f472b6',
  generic: '#94a3b8',
} as const;

const IMAGE_EXT = new Set(['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg', 'tif', 'tiff']);

const IMAGE_ICON_EXT = new Set([...IMAGE_EXT, 'heic', 'heif']);
const IMAGE_VIEWER_BLOCKLIST = new Set(['heic', 'heif']);
const VIDEO_EXT = new Set(['mp4', 'mov', 'webm', 'mkv']);
const AUDIO_EXT = new Set(['mp3', 'wav', 'm4a', 'aac']);
const TEXT_EXT = new Set(['txt', 'csv', 'log']);
const PDF_EXT = new Set(['pdf']);
const OFFICE_EXT = new Set(['doc', 'docx', 'xls', 'xlsx']);
const OFFICE_CONTENT_TYPES = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

type FileType = { Icon: LucideIcon; color: string };

const FILE_TYPES: Record<string, FileType> = {
  pdf: { Icon: FileText, color: COLOR.pdf },
  doc: { Icon: FileText, color: COLOR.doc },
  docx: { Icon: FileText, color: COLOR.doc },
  rtf: { Icon: FileText, color: COLOR.doc },
  txt: { Icon: FileText, color: COLOR.generic },
  xls: { Icon: FileSpreadsheet, color: COLOR.sheet },
  xlsx: { Icon: FileSpreadsheet, color: COLOR.sheet },
  csv: { Icon: FileSpreadsheet, color: COLOR.sheet },
  zip: { Icon: FileArchive, color: COLOR.archive },
  rar: { Icon: FileArchive, color: COLOR.archive },
  '7z': { Icon: FileArchive, color: COLOR.archive },
  gz: { Icon: FileArchive, color: COLOR.archive },
  mp3: { Icon: FileAudio, color: COLOR.audio },
  wav: { Icon: FileAudio, color: COLOR.audio },
  m4a: { Icon: FileAudio, color: COLOR.audio },
  aac: { Icon: FileAudio, color: COLOR.audio },
  mp4: { Icon: FileVideo, color: COLOR.video },
  mov: { Icon: FileVideo, color: COLOR.video },
  avi: { Icon: FileVideo, color: COLOR.video },
  mkv: { Icon: FileVideo, color: COLOR.video },
};

function extensionOf(fileName: string | undefined): string | undefined {
  if (!fileName) {
    return undefined;
  }
  const match = /\.([a-z0-9]+)$/i.exec(fileName.trim());
  return match?.[1]?.toLowerCase();
}

function isImageAttachment(attachment: EventAttachment, ext: string | undefined): boolean {
  if (attachment.contentType?.toLowerCase().startsWith('image/')) {
    return true;
  }
  return ext !== undefined && IMAGE_ICON_EXT.has(ext);
}

function viewerFromContentType(contentType: string | undefined): AttachmentViewer | undefined {
  if (!contentType) {
    return undefined;
  }
  const normalized = contentType.toLowerCase();
  if (normalized.startsWith('image/')) {
    const subtype = normalized.split('/')[1]?.split(';')[0]?.trim();
    return subtype && IMAGE_VIEWER_BLOCKLIST.has(subtype) ? 'none' : 'image';
  }
  if (normalized === 'application/pdf') {
    return 'pdf';
  }
  if (normalized.startsWith('video/')) {
    return 'video';
  }
  if (normalized.startsWith('audio/')) {
    return 'audio';
  }
  if (normalized.startsWith('text/')) {
    return 'text';
  }
  if (OFFICE_CONTENT_TYPES.includes(normalized)) {
    return 'none';
  }
  return undefined;
}

function viewerFromExtension(ext: string | undefined): AttachmentViewer {
  if (!ext) {
    return 'none';
  }
  if (IMAGE_VIEWER_BLOCKLIST.has(ext)) {
    return 'none';
  }
  if (IMAGE_EXT.has(ext)) {
    return 'image';
  }
  if (PDF_EXT.has(ext)) {
    return 'pdf';
  }
  if (VIDEO_EXT.has(ext)) {
    return 'video';
  }
  if (AUDIO_EXT.has(ext)) {
    return 'audio';
  }
  if (TEXT_EXT.has(ext)) {
    return 'text';
  }
  if (OFFICE_EXT.has(ext)) {
    return 'none';
  }
  return 'none';
}

/** Avgjør om vedlegget er et bilde, og ellers hvilket formatikon + farge som passer. */
export function attachmentKind(attachment: EventAttachment): AttachmentKind {
  const ext = extensionOf(attachment.fileName);
  const viewer = viewerFromContentType(attachment.contentType) ?? viewerFromExtension(ext);
  if (isImageAttachment(attachment, ext)) {
    return { isImage: viewer === 'image', viewer, Icon: FileImage, color: COLOR.image };
  }
  const fileType = ext ? FILE_TYPES[ext] : undefined;
  return {
    isImage: viewer === 'image',
    viewer,
    Icon: fileType?.Icon ?? FileIcon,
    color: fileType?.color ?? COLOR.generic,
  };
}
